import type { ReactElement } from 'react';
import 'cross-fetch';
import { Component } from 'react';

import debounce from 'lodash/debounce';

import {
  buildAdditionalInfoAutoSuggestPois,
  buildResultsUpdatedMessage,
} from '../../utils/mini-events';

import AutosuggestGateway from './autosuggest-gateway';

import type { AutosuggestGateway as AutosuggestGatewayType } from './types';
import type {
  AutosuggestSuggestionsOrPopularDestinations,
  PopularDestination,
  AutosuggestSuggestion,
} from '../../types';
import type { Configs } from '@shared/types/configs';
import type { Culture } from '@shared/types/context';
import type { MiniEventTracker } from '@shared/types/frontend/mini-event-tracker';
import type { I18nService } from '@shared/types/i18n';
import type { UserContext } from '@shared/types/user-context';
import type { Maybe } from '@shared/types/utils';

import { RESULTS_UPDATED } from '@shared/constants/newrelic-events/event-names';
import { withI18n } from '@shared/ui/contexts/i18n';
import { withMiniEventTracker } from '@shared/ui/contexts/mini-events';
import { withConfiguration } from '@shared/utils-frontend/contexts/ConfigurationContext';
import { withUserContext } from '@shared/utils-frontend/contexts/UserContext';
import getDeviceType from '@shared/utils-frontend/get-device-type';
import logger from '@shared/utils-frontend/logger';
import { ACTION_TYPE } from '@shared/utils-frontend/mini-events/constants';

type Props = {
  i18n: I18nService;
  children: (
    suggestions: AutosuggestSuggestionsOrPopularDestinations,
    onClearSuggestions: () => void,
  ) => ReactElement;
  input?: Maybe<string>;
  miniEventTracker: MiniEventTracker;
  userContext: UserContext;
  configs: Configs;
};

type State = {
  input: Maybe<string>;
  suggestions: AutosuggestSuggestionsOrPopularDestinations;
  emptyInputSuggestions: AutosuggestSuggestionsOrPopularDestinations;
};

const AUTOSUGGEST_RESULTS_UPDATED_DEBOUNCE_MILLI = 650;
class AutosuggestDataProvider extends Component<Props, State> {
  autosuggestGateway: AutosuggestGatewayType;

  state: State = {
    input: this.props.input,
    suggestions: [],
    emptyInputSuggestions: [],
  };

  buildAndSendSelectionUpdatedEvent = debounce(
    (
      query: Maybe<string>,
      numResults: number,
      culture: Culture,
      suggestions: AutosuggestSuggestionsOrPopularDestinations,
    ) => {
      const entities = suggestions.map(
        (suggestion: AutosuggestSuggestion | PopularDestination) => {
          if ('entityId' in suggestion) {
            return suggestion.entityId;
          }
          return 'popularType';
        },
      );
      const suggestedEntityIds = entities
        .filter((item: string) => item !== 'popularType')
        .join(',');
      const { miniEventTracker } = this.props;
      const message = buildResultsUpdatedMessage(
        query,
        numResults,
        culture,
        suggestedEntityIds,
      );
      miniEventTracker.trackAutoSuggest(message);
      logger.event(RESULTS_UPDATED);
    },
    AUTOSUGGEST_RESULTS_UPDATED_DEBOUNCE_MILLI,
  );

  constructor(props: Props) {
    super(props);
    const {
      configs,
      i18n: { culture },
      miniEventTracker,
      userContext: { device, pageType, userPreferences },
    } = props;

    const platform = getDeviceType(device);

    this.autosuggestGateway = AutosuggestGateway({
      culture,
      platform,
      pageType,
      utid: userPreferences.utid,
      autosuggestExpVal: configs.autosuggestExpVal,
      trackExperimentChokepoint: miniEventTracker
        ? miniEventTracker.trackExperimentChokepoint
        : () => {},
    });
  }

  componentDidMount() {
    this.autosuggestGateway.subscribe({
      next: (suggestions) => {
        this.setState({
          suggestions,
          emptyInputSuggestions: suggestions,
        });

        const {
          i18n: { culture },
          input: query,
          miniEventTracker,
        } = this.props;
        const numResults = suggestions.length;
        const [firstItem] = suggestions;
        this.buildAndSendSelectionUpdatedEvent(
          query,
          numResults,
          culture,
          suggestions,
        );

        const item = firstItem as AutosuggestSuggestion;
        if (item.pois && item.pois.length > 0) {
          miniEventTracker.trackHotelsAction(
            ACTION_TYPE.AUTO_SUGGEST_POIS_RENDER,
            buildAdditionalInfoAutoSuggestPois(
              firstItem as AutosuggestSuggestion,
            ),
          );
        }
      },

      error: () => {
        this.setState({ suggestions: [] });
      },
    });
  }

  static getDerivedStateFromProps(nextProps: Props, prevState: State) {
    const { emptyInputSuggestions, input: inputFromState } = prevState;
    const suggestion = emptyInputSuggestions[0] as PopularDestination;
    if (
      nextProps.input === '' &&
      !inputFromState &&
      suggestion &&
      suggestion.popularDestinations &&
      suggestion.popularDestinations.length > 0
    ) {
      return { suggestions: emptyInputSuggestions };
    }
    return null;
  }

  componentDidUpdate({ input: prevInput }: Pick<Props, 'input'>) {
    const { input } = this.props;
    if (prevInput?.trim() === input?.trim()) {
      return;
    }

    this.autosuggestGateway.input({ value: input });
  }

  componentWillUnmount() {
    this.autosuggestGateway.unsubscribe();
  }

  onSuggestionsClearRequested = () => {
    this.setState({
      suggestions: [],
    });
  };

  render() {
    const { suggestions } = this.state;

    const { children } = this.props;
    return <>{children(suggestions, this.onSuggestionsClearRequested)}</>;
  }
}

export default withConfiguration(
  withMiniEventTracker(withI18n(withUserContext(AutosuggestDataProvider))),
);
